import { Sparkles } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { LoadingSpinner } from "~/components/Icons";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { trackEvent } from "~/utils/analytics";

const MAX_INSTRUCTIONS_LENGTH = 100;

type FormatSectionInputProps = {
  sectionId: string;
  sectionTitle: string;
  isFormatting: boolean;
  disabled?: boolean;
  onFormat: (instructions: string) => void;
  /**
   * Analytics event fired on Apply click. Defaults to the custom-reports
   * section-format event; override for other surfaces (e.g. Summary tab).
   */
  analyticsEventName?: string;
};

export const FormatSectionInput = ({
  sectionId,
  sectionTitle,
  isFormatting,
  disabled,
  onFormat,
  analyticsEventName = "app/report/section_format.button/click",
}: FormatSectionInputProps) => {
  const { formatMessage } = useIntl();
  const [instructions, setInstructions] = useState("Tidy up");
  const [open, setOpen] = useState(false);

  const handleApply = () => {
    trackEvent({
      eventName: analyticsEventName,
      properties: {
        sectionId,
        sectionTitle,
        instructions,
        hasCustomInstructions: instructions !== "Tidy up",
      },
    });
    onFormat(instructions);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
          disabled={disabled || isFormatting}
        >
          {isFormatting ? (
            <LoadingSpinner className="w-4 h-4 mr-1" />
          ) : (
            <Sparkles className="w-4 h-4 mr-1" />
          )}
          <FormattedMessage id="report.format" defaultMessage="Format" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={5}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Input
              value={instructions}
              onChange={(e) =>
                setInstructions(
                  e.target.value.slice(0, MAX_INSTRUCTIONS_LENGTH)
                )
              }
              placeholder={formatMessage({ id: "report.format.placeholder" })}
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply();
              }}
            />
            <Button
              size="sm"
              className="h-8 shrink-0"
              onClick={handleApply}
              disabled={isFormatting}
            >
              <FormattedMessage
                id="report.format.apply"
                defaultMessage="Apply"
              />
            </Button>
          </div>
          <span className="text-[10px] text-muted-foreground text-right">
            {instructions.length}/{MAX_INSTRUCTIONS_LENGTH}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
